import { ChangeEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, MenuItem, Pagination, Select } from "@mui/material";
import { PrimaryTable } from "@/components/tables/primary-table";
import { GoMakeAutoComplate } from "@/components";
import { GoMakeMultiSelect } from "@/components/auto-complete/multi-select";
import { ControlledDateRangePicker } from "@/components/datepicker/controlled-date-range-picker";
import { PermissionCheck } from "@/components/CheckPermission";
import { Permissions } from "@/components/CheckPermission/enum";
import { DOCUMENT_TYPE, INVOICES_EXPORT_TYPES } from "@/pages-components/quotes/enums";
import { ExcelMenu } from "@/pages-components/quotes/widgets/excel-menu/excel-menu";
import { GmIcon } from "./gm-icon";

/**
 * Modern home › per-customer document list (one per tab).
 * Pure presentation: all state and handlers come from `useQuotes` through
 * `QuotesListPageWidget` (home mode, `homeVariant="modern"`), so row actions,
 * modals, export and "new quote" keep the exact behaviour of the document pages.
 */

interface IProps {
  documentType: DOCUMENT_TYPE;
  rows: any[];
  headers: ReactNode[];
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  pagesCount: number;
  totalItems: number;
  onChangePageSize: (event: any) => void;
  searchValue: string | null | undefined;
  onSearch: (value: string) => void;
  fromDate?: Date;
  toDate?: Date;
  onSelectDates: (value: [Date | null, Date | null]) => void;
  statusOptions: { label: string; value: any }[];
  statusValue: any;
  onChangeStatus: (value: any) => void;
  productsList: { label: string; value: string }[];
  productIds: string[];
  onChangeProducts: (values: string[]) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
  onCreateNew: () => void;
  onExportTable: (exportType?: INVOICES_EXPORT_TYPES) => void;
  onExportOrderItems?: () => void;
}

const PAGE_SIZES = [10, 15, 20, 30, 50];
const SEARCH_DEBOUNCE_MS = 400;

const ModernHomeDocList = ({
  documentType,
  rows,
  headers,
  isLoading,
  page,
  setPage,
  pageSize,
  pagesCount,
  totalItems,
  onChangePageSize,
  searchValue,
  onSearch,
  fromDate,
  toDate,
  onSelectDates,
  statusOptions,
  statusValue,
  onChangeStatus,
  productsList,
  productIds,
  onChangeProducts,
  hasFilters,
  onClearFilters,
  onCreateNew,
  onExportTable,
  onExportOrderItems,
}: IProps) => {
  const { t } = useTranslation();
  const tr = (key: string, fallback: string) => t(key, { defaultValue: fallback });
  const docLabel = t(`sales.list.${DOCUMENT_TYPE[documentType]}`);
  const isQuote = documentType === DOCUMENT_TYPE.quote;

  // Debounced search: type freely, fetch once typing pauses.
  const [search, setSearch] = useState<string>(searchValue ? String(searchValue) : "");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setSearch(searchValue ? String(searchValue) : "");
  }, [searchValue]);
  const onSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearch(value.trim()), SEARCH_DEBOUNCE_MS);
  };
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const first = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalItems);
  const lastColumn = headers.length - 1;

  return (
    <div className="gm-doclist-body">
      <div className="gm-doclist-toolbar">
        <label className="gm-doclist-search">
          <GmIcon name="search" size={16} />
          <input
            type="search"
            value={search}
            onChange={onSearchInput}
            placeholder={t("modernHome.searchDocuments", {
              defaultValue: "Search {{documents}}...",
              documents: docLabel.toLowerCase(),
            })}
            aria-label={t("sales.quote.search")}
          />
        </label>

        <div className="gm-doclist-filter gm-doclist-filter--dates">
          <ControlledDateRangePicker
            startDate={fromDate ?? null}
            endDate={toDate ?? null}
            placeholder={t("boardMissions.dateRange")}
            onChange={(start, end) => onSelectDates([start, end])}
            allowClear
            allowManualInput
          />
        </div>

        <div className="gm-doclist-filter gm-doclist-filter--status">
          <GoMakeAutoComplate
            key={statusValue?.value ?? "none"}
            options={statusOptions}
            getOptionLabel={(option: any) => option?.label || ""}
            placeholder={t("sales.quote.status")}
            value={statusValue ?? null}
            onChange={(_: any, value: any) => onChangeStatus(value)}
          />
        </div>

        {productsList?.length > 0 && (
          <div className="gm-doclist-filter gm-doclist-filter--products">
            <GoMakeMultiSelect
              options={productsList}
              values={productIds}
              onChange={onChangeProducts}
              placeholder={t("home.headers.jobName")}
            />
          </div>
        )}

        {hasFilters && (
          <button type="button" className="gm-doclist-clear" onClick={onClearFilters}>
            {tr("modernHome.clearFilters", "Clear filters")}
          </button>
        )}

        <div className="gm-doclist-toolbar-end">
          {isQuote && (
            <PermissionCheck userPermission={Permissions.CREATE_QUOTES}>
              <button type="button" className="gm-doclist-new" onClick={onCreateNew}>
                <GmIcon name="plus" size={16} />
                {t("sales.quote.createNew")}
              </button>
            </PermissionCheck>
          )}
          <div className="gm-doclist-export">
            <ExcelMenu
              documentType={documentType}
              onExportTableClick={onExportTable}
              onExportOrderItemsClick={onExportOrderItems}
            />
          </div>
        </div>
      </div>

      <div className="gm-doclist-table" aria-busy={isLoading}>
        {isLoading && (
          <div className="gm-doclist-loading">
            <CircularProgress size={22} />
          </div>
        )}
        <PrimaryTable
          stickyHeader
          rows={rows}
          headers={headers}
          variant="OddEvenTable"
          withoutShadow
          columnWidths={headers.map((_, i) => (i === 0 ? "48px" : i === lastColumn ? "56px" : undefined))}
          columnAlignments={headers.map((_, i) => (i === lastColumn ? "center" : undefined))}
          emptyState={{
            title: `${t("general.tableEmptyStateTitle", {
              document: docLabel.toLowerCase(),
            })}`,
          }}
        />
      </div>

      <div className="gm-doclist-footer">
        <label className="gm-doclist-rows">
          <span>{tr("modernHome.rowsPerPage", "Rows per page")}</span>
          <Select
            variant="standard"
            disableUnderline
            value={pageSize}
            onChange={onChangePageSize}
            MenuProps={{ disablePortal: false }}
          >
            {PAGE_SIZES.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </label>
        <span className="gm-doclist-range">
          {first}–{last} {tr("modernHome.of", "of")} {totalItems}
        </span>
        <Pagination
          count={Math.max(pagesCount, 1)}
          page={page}
          onChange={(_, value) => setPage(value)}
          shape="rounded"
          size="small"
          siblingCount={1}
          boundaryCount={1}
        />
      </div>
    </div>
  );
};

export { ModernHomeDocList };
